import { useState, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthShell from '@/pages/auth/components/AuthShell';
import Input from '@/components/base/Input';
import Button from '@/components/base/Button';
import Toast from '@/components/base/Toast';
import Modal from '@/components/base/Modal';

interface FormState {
  email: string;
  password: string;
}

const initialForm: FormState = { email: '', password: '' };

export default function LoginPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState<1 | 2>(1);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const next: Partial<FormState> = {};
    if (!form.email.trim()) next.email = 'البريد الإلكتروني مطلوب';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = 'يرجى إدخال بريد إلكتروني صحيح';
    if (!form.password) next.password = 'كلمة المرور مطلوبة';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate('/dashboard');
    }, 900);
  };

  const handleSendCode = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail.trim())) {
      setResetError('يرجى إدخال بريد إلكتروني صحيح');
      return;
    }
    setResetError('');
    setResetStep(2);
  };

  const handleResetPassword = () => {
    if (resetCode.trim().length < 4) {
      setResetError('كود التحقق غير صحيح');
      return;
    }
    if (newPassword.length < 6) {
      setResetError('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('كلمتا المرور غير متطابقتين');
      return;
    }
    setResetError('');
    setResetOpen(false);
    setResetStep(1);
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setToast('تم تعيين كلمة مرور جديدة بنجاح');
  };

  return (
    <AuthShell>
      {toast && (
        <Toast type="success" message={toast} onClose={() => setToast(null)} />
      )}

      <h1 className="font-bold text-2xl text-foreground-950">تسجيل الدخول</h1>
      <p className="mt-2 text-sm text-foreground-500">مرحباً بك في لوحة التحكم</p>

      <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
        <Input
          label="البريد الإلكتروني"
          name="email"
          type="email"
          icon="ri-mail-line"
          placeholder="example@mail.com"
          value={form.email}
          error={errors.email}
          onChange={(event) => update('email', event.target.value)}
        />

        <div>
          <Input
            label="كلمة المرور"
            name="password"
            type={showPassword ? 'text' : 'password'}
            icon="ri-lock-line"
            placeholder="كلمة المرور"
            value={form.password}
            error={errors.password}
            onChange={(event) => update('password', event.target.value)}
            trailing={
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                <i
                  className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}
                  aria-hidden="true"
                ></i>
              </button>
            }
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => setResetOpen(true)}
              className="font-medium text-[13px] text-secondary-600 transition-colors hover:text-secondary-700"
            >
              نسيت كلمة المرور؟
            </button>
          </div>
        </div>

        <div className="pt-1">
          <Button type="submit" variant="cta" size="lg" fullWidth loading={loading}>
            تسجيل الدخول
          </Button>
        </div>
      </form>

      <div className="my-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-background-200"></span>
        <span className="text-[13px] text-foreground-400">أو</span>
        <span className="h-px flex-1 bg-background-200"></span>
      </div>

      <Button
        type="button"
        variant="outline"
        size="lg"
        fullWidth
        icon="ri-store-2-line"
        onClick={() => navigate('/auth/register')}
      >
        تسجيل مغسلة جديدة
      </Button>

      <p className="mt-6 text-center text-[13px] text-foreground-500">
        لديك حساب قيد المراجعة؟{' '}
        <Link
          to="/auth/under-review"
          className="font-medium text-secondary-600 hover:text-secondary-700"
        >
          معرفة الحالة
        </Link>
      </p>

      <Modal
        open={resetOpen}
        title="استعادة كلمة المرور"
        subtitle={
          resetStep === 1
            ? 'أدخل بريدك الإلكتروني وسنرسل لك كود تحقق'
            : 'أدخل الكود ثم كلمة المرور الجديدة'
        }
        onClose={() => {
          setResetOpen(false);
          setResetStep(1);
          setResetError('');
        }}
        footer={
          <Button
            variant="cta"
            size="lg"
            fullWidth
            onClick={resetStep === 1 ? handleSendCode : handleResetPassword}
          >
            {resetStep === 1 ? 'إرسال كود التحقق' : 'تعيين كلمة المرور'}
          </Button>
        }
      >
        <div className="space-y-4">
          <Input
            label="البريد الإلكتروني"
            name="reset_email"
            type="email"
            icon="ri-mail-line"
            placeholder="example@mail.com"
            value={resetEmail}
            disabled={resetStep === 2}
            onChange={(event) => {
              setResetEmail(event.target.value);
              setResetError('');
            }}
          />

          {resetStep === 2 && (
            <>
              <Input
                label="كود التحقق"
                name="reset_code"
                icon="ri-shield-keyhole-line"
                placeholder="1234"
                value={resetCode}
                onChange={(event) => {
                  setResetCode(event.target.value);
                  setResetError('');
                }}
              />
              <Input
                label="كلمة المرور الجديدة"
                name="new_password"
                type="password"
                icon="ri-lock-line"
                placeholder="••••••••"
                value={newPassword}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  setResetError('');
                }}
              />
              <Input
                label="تأكيد كلمة المرور"
                name="confirm_password"
                type="password"
                icon="ri-lock-2-line"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  setResetError('');
                }}
              />
            </>
          )}

          {resetError && (
            <p className="flex items-center gap-1.5 text-[12px] text-danger-600">
              <i className="ri-error-warning-line text-sm" aria-hidden="true"></i>
              {resetError}
            </p>
          )}
        </div>
      </Modal>
    </AuthShell>
  );
}